import { useState, useEffect, useCallback } from 'react';
import { Camera, LogOut, Scale, RefreshCw, Clock } from 'lucide-react';
import { Layout } from '../components/Layout';
import { ModeToggle } from '../components/ModeToggle';
import { SearchBar } from '../components/SearchBar';
import { CardGrid } from '../components/CardGrid';
import { CardDetail } from '../components/CardDetail';
import { BottomNav } from '../components/BottomNav';
import { SplitView } from '../components/SplitView';
import { CardDetailPanel } from '../components/CardDetailPanel';
import { EmptyDetailState } from '../components/EmptyDetailState';
import { ComparisonBar } from '../components/ComparisonBar';
import { ComparisonPanel } from '../components/ComparisonPanel';
import { ConnectionIndicator } from '../components/ConnectionIndicator';
import { OfflineBanner } from '../components/OfflineBanner';
import { CacheBadge } from '../components/CacheBadge';
import { CartBar } from '../components/CartBar';
import { CartDrawer } from '../components/CartDrawer';
import { ShowTotalOverlay } from '../components/ShowTotalOverlay';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SessionHistory } from '../components/SessionHistory';
import { CameraCapture } from '../components/CameraCapture';
import { useAuth } from '../hooks/useAuth';
import { useResilientSearch } from '../hooks/useResilientSearch';
import { useSettings } from '../hooks/useSettings';
import { useDevice } from '../hooks/useDevice';
import { useCart } from '../hooks/useCart';
import { useSessionHistory } from '../hooks/useSessionHistory';
import { track } from '../lib/telemetry';
import type { Card, Mode, CartItem } from '../lib/types';

export function Home() {
  const { logout } = useAuth();
  const { settings, updateBuyOfferPercent } = useSettings();
  const {
    query,
    cards,
    isLoading,
    error,
    cacheHit,
    parsedQuery,
    handleQueryChange,
    clearSearch,
    isStale,
    cacheTimestamp,
    isRevalidating,
    retry,
  } = useResilientSearch();
  const { isTablet, isPhone } = useDevice();
  const cart = useCart();
  const history = useSessionHistory();

  // Always boot into buy mode — TCG events are buy-mode-heavy (customers
  // selling cards at the booth, or batch trade-in price quotes). Operators
  // can toggle to sell on demand. The stored settings.defaultMode field is
  // kept for future per-operator preference but isn't read at boot.
  const [mode, setMode] = useState<Mode>('buy');
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  // True when the currently-selected card was opened via the scan modal's
  // "Quote price" path. Suppresses the Add-to-Cart action in CardDetail /
  // CardDetailPanel since the operator wanted a price-check, not a commit.
  const [selectedCardQuoteOnly, setSelectedCardQuoteOnly] = useState(false);

  // Card-grid taps always restore full Add-to-Cart capability — only scans
  // through the explicit "Quote price" button keep the quote-only flag set.
  const handleSelectCardFromGrid = useCallback((card: Card) => {
    setSelectedCard(card);
    setSelectedCardQuoteOnly(false);
    track('card_viewed', {
      card_id: card.id,
      card_name: card.name,
      set_name: card.set_name,
      source: 'search',
    });
  }, []);

  // Comparison state (tablet only)
  const [comparisonCards, setComparisonCards] = useState<Card[]>([]);
  const [isComparing, setIsComparing] = useState(false);

  // Cart UI state
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isShowingTotal, setIsShowingTotal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Camera / scan UI state
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  // Mode switch warning
  const [pendingModeSwitch, setPendingModeSwitch] = useState<Mode | null>(null);

  // Clear comparison when switching to phone mode
  useEffect(() => {
    if (isPhone && comparisonCards.length > 0) {
      setComparisonCards([]);
      setIsComparing(false);
    }
  }, [isPhone, comparisonCards.length]);

  // Handle mode change with cart warning
  const handleModeChange = useCallback(
    (newMode: Mode) => {
      if (
        cart.activeSession?.items.length &&
        cart.activeSession.mode !== newMode
      ) {
        setPendingModeSwitch(newMode);
        return;
      }
      setMode(newMode);
      track('mode_changed', { mode: newMode, parked: false });
    },
    [cart.activeSession]
  );

  const confirmModeSwitch = useCallback(() => {
    if (!pendingModeSwitch) return;
    cart.parkSession();
    setMode(pendingModeSwitch);
    track('mode_changed', { mode: pendingModeSwitch, parked: true });
    setPendingModeSwitch(null);
  }, [pendingModeSwitch, cart]);

  // Handle adding/removing cards from comparison
  const handleToggleCompare = (card: Card) => {
    setComparisonCards((prev) => {
      const isAlreadySelected = prev.some((c) => c.id === card.id);
      if (isAlreadySelected) {
        return prev.filter((c) => c.id !== card.id);
      }
      if (prev.length < 3) {
        track('compare_added', {
          card_id: card.id,
          card_name: card.name,
          comparison_size: prev.length + 1,
        });
        return [...prev, card];
      }
      return prev;
    });
  };

  const handleClearComparison = () => {
    setComparisonCards([]);
    setIsComparing(false);
  };

  const handleRemoveFromComparison = (card: Card) => {
    setComparisonCards((prev) => prev.filter((c) => c.id !== card.id));
    if (comparisonCards.length <= 1) {
      setIsComparing(false);
    }
  };

  // Cart handlers
  const handleAddToCart = useCallback(
    (item: Omit<CartItem, 'id'>) => {
      if (!cart.activeSession) {
        cart.startNewSession(mode);
      }
      cart.addItem(item);
    },
    [cart, mode]
  );

  const handleCompleteSession = useCallback(() => {
    cart.completeSession();
    setIsCartOpen(false);
    // Refresh history so it's up to date if they open it
    history.refresh();
  }, [cart, history]);

  const handleClearCart = useCallback(() => {
    setShowClearConfirm(true);
  }, []);

  const confirmClearCart = useCallback(() => {
    cart.clearCart();
    setShowClearConfirm(false);
    setIsCartOpen(false);
  }, [cart]);

  const handleShowTotal = useCallback(() => {
    setIsCartOpen(false);
    setIsShowingTotal(true);
  }, []);

  const handleOpenHistory = useCallback(() => {
    history.refresh();
    setIsHistoryOpen(true);
  }, [history]);

  const handleCartLongPress = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  // Scan handlers
  const handleScanOpen = useCallback(() => {
    setIsCameraOpen(true);
  }, []);

  // Sell-mode scan result: open the standard CardDetail flow.
  const handleScanCardSelected = useCallback((card: Card) => {
    setSelectedCard(card);
    setSelectedCardQuoteOnly(false);
    setIsCameraOpen(false);
    track('card_viewed', {
      card_id: card.id,
      card_name: card.name,
      set_name: card.set_name,
      source: 'scan',
    });
  }, []);

  // Quote-only scan result: open CardDetail in read-only mode (no Add-to-Cart).
  const handleScanCardForQuote = useCallback((card: Card) => {
    setSelectedCard(card);
    setSelectedCardQuoteOnly(true);
    setIsCameraOpen(false);
    track('card_viewed', {
      card_id: card.id,
      card_name: card.name,
      set_name: card.set_name,
      source: 'scan_quote',
    });
  }, []);

  // Buy-mode scan result: fast-add straight to the active cart session.
  const handleScanAddToCart = useCallback(
    (item: Omit<CartItem, 'id'>) => {
      handleAddToCart(item);
      setIsCameraOpen(false);
    },
    [handleAddToCart]
  );

  // No-match path: drop recognized text into the search bar.
  const handleScanFallback = useCallback(
    (query: string) => {
      setIsCameraOpen(false);
      handleQueryChange(query);
    },
    [handleQueryChange]
  );

  const hasCartItems = cart.itemCount > 0;

  // Results content (shared between phone and tablet)
  const renderResults = () => (
    <>
      {/* Error state with retry button */}
      {error && !isStale && (
        <div className="mb-4 rounded-lg bg-destructive/12 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center justify-between gap-3">
            <span>{error}</span>
            <button
              onClick={retry}
              disabled={isLoading}
              className="flex items-center gap-1.5 rounded-full bg-destructive/20 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/30 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Stale data warning (when showing cached data after revalidation failed) */}
      {isStale && error && (
        <div className="mb-4 rounded-lg border border-yellow-600/40 bg-yellow-50 px-4 py-3 text-sm text-yellow-700">
          <div className="flex items-center justify-between gap-3">
            <span>Showing cached results. {error}</span>
            <button
              onClick={retry}
              disabled={isLoading || isRevalidating}
              className="flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1.5 text-xs font-medium text-yellow-800 hover:bg-yellow-200 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-3 w-3 ${isLoading || isRevalidating ? 'animate-spin' : ''}`} />
              Retry
            </button>
          </div>
        </div>
      )}

      {!query && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-full bg-muted p-4">
            <svg
              className="h-12 w-12 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <p className="text-foreground">Search for Pokemon TCG cards</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter a card name, set, or number
          </p>
        </div>
      )}

      {query && !isLoading && cards.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-foreground">No cards found</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Try a different search term
          </p>
        </div>
      )}

      {cards.length > 0 && (
        <>
          <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {cards.length} result{cards.length !== 1 ? 's' : ''}
              {isRevalidating && (
                <span className="ml-2 text-muted-foreground">
                  <RefreshCw className="inline h-3 w-3 animate-spin" />
                </span>
              )}
            </span>
            {cacheHit && (
              <CacheBadge timestamp={cacheTimestamp} isStale={isStale} />
            )}
          </div>
          <CardGrid
            cards={cards}
            mode={mode}
            buyPercent={settings.buyOfferPercent}
            onCardSelect={handleSelectCardFromGrid}
            selectedCardId={isTablet ? selectedCard?.id : undefined}
            isTablet={isTablet}
            comparisonMode={isTablet && comparisonCards.length > 0}
            comparisonCards={comparisonCards}
            onToggleCompare={handleToggleCompare}
          />
        </>
      )}
    </>
  );

  // Phone mode layout
  if (isPhone) {
    return (
      <Layout hideHeader>
        {/* Header with app title, connection indicator, and logout */}
        <div className="sticky top-0 z-40 border-b border-outline bg-primary px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-primary-foreground">TCG Price Lookup</h1>
              <ConnectionIndicator />
            </div>
            <button
              onClick={logout}
              className="rounded-full p-2 text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground transition-colors"
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Offline banner */}
        <OfflineBanner />

        {/* Content area with bottom padding for nav + cart bar */}
        <div className={`flex-1 overflow-auto p-4 ${hasCartItems ? 'pb-48' : 'pb-36'}`}>
          {renderResults()}
        </div>

        {/* Cart bar (phone) */}
        {cart.activeSession && (
          <CartBar
            mode={cart.activeSession.mode}
            itemCount={cart.itemCount}
            total={cart.total}
            parkedCount={cart.parkedSessions.length}
            onExpand={() => setIsCartOpen(true)}
            onLongPress={handleCartLongPress}
            variant="phone"
          />
        )}

        {/* Bottom navigation */}
        <BottomNav
          query={query}
          onQueryChange={handleQueryChange}
          onClear={clearSearch}
          isLoading={isLoading}
          interpretation={parsedQuery?.interpretation}
          mode={mode}
          onModeChange={handleModeChange}
          buyPercent={settings.buyOfferPercent}
          onBuyPercentChange={updateBuyOfferPercent}
          onOpenHistory={handleOpenHistory}
          onScanOpen={handleScanOpen}
        />

        {/* Card detail sheet (phone mode).
            key={selectedCard.id}: forces a clean remount when the user
            switches to a different card. Without this, useState-initialized
            internal state (selectedCondition, selectedEdition, the chart's
            duration default) would persist from the previous card. */}
        {selectedCard && (
          <CardDetail
            key={selectedCard.id}
            card={selectedCard}
            mode={mode}
            buyPercent={settings.buyOfferPercent}
            onClose={() => {
              setSelectedCard(null);
              setSelectedCardQuoteOnly(false);
            }}
            onAddToCart={selectedCardQuoteOnly ? undefined : handleAddToCart}
          />
        )}

        {/* Cart drawer (phone) */}
        {isCartOpen && cart.activeSession && (
          <CartDrawer
            session={cart.activeSession}
            mode={cart.activeSession.mode}
            onClose={() => setIsCartOpen(false)}
            onRemoveItem={cart.removeItem}
            onUpdatePrice={cart.updateItemPrice}
            onShowTotal={handleShowTotal}
            onCompleteSession={handleCompleteSession}
            onClearCart={handleClearCart}
            parkedSessions={cart.parkedSessions}
            onSwitchSession={cart.resumeSession}
            onParkSession={cart.parkSession}
            onStartNewSession={cart.startNewSession}
            onRenameSession={cart.renameSession}
            variant="phone"
          />
        )}

        {/* Show total overlay */}
        {isShowingTotal && cart.activeSession && (
          <ShowTotalOverlay
            items={cart.activeSession.items}
            total={cart.total}
            itemCount={cart.itemCount}
            mode={cart.activeSession.mode}
            onDismiss={() => setIsShowingTotal(false)}
          />
        )}

        {/* Clear cart confirmation */}
        {showClearConfirm && (
          <ConfirmDialog
            title="Clear Cart?"
            message={`This will remove all ${cart.itemCount} item${cart.itemCount !== 1 ? 's' : ''} from the current session.`}
            confirmLabel="Clear"
            onConfirm={confirmClearCart}
            onCancel={() => setShowClearConfirm(false)}
          />
        )}

        {/* Mode switch warning */}
        {pendingModeSwitch && (
          <ConfirmDialog
            title={`Switch to ${pendingModeSwitch === 'buy' ? 'Buy' : 'Sell'} mode?`}
            message={`You have ${cart.itemCount} item${cart.itemCount !== 1 ? 's' : ''} in your ${cart.activeSession?.mode === 'buy' ? 'Buy' : 'Sell'} cart. This session will be parked.`}
            confirmLabel="Switch"
            confirmVariant="primary"
            onConfirm={confirmModeSwitch}
            onCancel={() => setPendingModeSwitch(null)}
          />
        )}

        {/* Session history */}
        {isHistoryOpen && (
          <SessionHistory
            sessions={history.sessions}
            isLoading={history.isLoading}
            onDeleteSession={history.deleteSession}
            onClearAll={history.clearAll}
            onCopyToClipboard={history.copyToClipboard}
            onClose={() => setIsHistoryOpen(false)}
          />
        )}

        {/* Card scan modal */}
        {isCameraOpen && (
          <CameraCapture
            mode={mode}
            buyPercent={settings.buyOfferPercent}
            onClose={() => setIsCameraOpen(false)}
            onCardSelected={handleScanCardSelected}
            onCardForQuote={handleScanCardForQuote}
            onAddToCart={handleScanAddToCart}
            onFallbackSearch={handleScanFallback}
          />
        )}
      </Layout>
    );
  }

  // Tablet mode layout
  return (
    <Layout hideHeader>
      <SplitView
        left={
          <div className="relative flex h-full flex-col">
            {/* Header bar */}
            <div className="sticky top-0 z-20 border-b border-outline bg-background p-4">
              {/* Top row: Title, connection indicator, and logout */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-semibold text-foreground">TCG Price Lookup</h1>
                  <ConnectionIndicator />
                </div>
                <div className="flex items-center gap-2">
                  {/* Scan button */}
                  <button
                    onClick={handleScanOpen}
                    className="rounded-full p-2 text-primary hover:bg-card hover:text-primary/80 transition-colors"
                    title="Scan a card"
                    aria-label="Scan a card"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  {/* History button */}
                  <button
                    onClick={handleOpenHistory}
                    className="rounded-full p-2 text-muted-foreground hover:bg-card hover:text-foreground transition-colors"
                    title="Session History"
                  >
                    <Clock className="h-4 w-4" />
                  </button>
                  {/* Comparison mode toggle */}
                  <button
                    onClick={() => {
                      if (comparisonCards.length > 0) {
                        handleClearComparison();
                      }
                    }}
                    className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-all ${
                      comparisonCards.length > 0
                        ? 'bg-primary/12 text-primary'
                        : 'bg-card text-muted-foreground hover:bg-muted'
                    }`}
                    title={comparisonCards.length > 0 ? 'Clear comparison' : 'Select cards to compare'}
                  >
                    <Scale className="h-4 w-4" />
                    Compare
                  </button>
                  <button
                    onClick={logout}
                    className="rounded-full p-2 text-muted-foreground hover:bg-card hover:text-foreground transition-colors"
                    title="Logout"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Mode toggle */}
              <div className="mb-4">
                <ModeToggle
                  mode={mode}
                  onModeChange={handleModeChange}
                  buyPercent={settings.buyOfferPercent}
                  onBuyPercentChange={updateBuyOfferPercent}
                />
              </div>

              {/* Search bar */}
              <SearchBar
                query={query}
                onQueryChange={handleQueryChange}
                onClear={clearSearch}
                isLoading={isLoading}
                interpretation={parsedQuery?.interpretation}
              />
            </div>

            {/* Offline banner */}
            <OfflineBanner />

            {/* Results area */}
            <div className="flex-1 overflow-auto p-4 pb-20">
              {renderResults()}
            </div>

            {/* Cart bar (tablet) */}
            {cart.activeSession && (
              <CartBar
                mode={cart.activeSession.mode}
                itemCount={cart.itemCount}
                total={cart.total}
                parkedCount={cart.parkedSessions.length}
                onExpand={() => setIsCartOpen(true)}
                onLongPress={handleCartLongPress}
                variant="tablet"
              />
            )}

            {/* Comparison bar */}
            {comparisonCards.length > 0 && (
              <ComparisonBar
                cards={comparisonCards}
                onCompare={() => {
                  track('compare_opened', { comparison_size: comparisonCards.length });
                  setIsComparing(true);
                }}
                onClear={handleClearComparison}
              />
            )}
          </div>
        }
        right={
          selectedCard ? (
            <CardDetailPanel
              key={selectedCard.id}
              card={selectedCard}
              mode={mode}
              buyPercent={settings.buyOfferPercent}
              onAddToCompare={handleToggleCompare}
              comparisonCards={comparisonCards}
              onAddToCart={selectedCardQuoteOnly ? undefined : handleAddToCart}
            />
          ) : (
            <EmptyDetailState />
          )
        }
      />

      {/* Comparison panel modal */}
      {isComparing && comparisonCards.length >= 2 && (
        <ComparisonPanel
          cards={comparisonCards}
          mode={mode}
          buyPercent={settings.buyOfferPercent}
          onRemoveCard={handleRemoveFromComparison}
          onClose={() => setIsComparing(false)}
        />
      )}

      {/* Cart drawer (tablet) */}
      {isCartOpen && cart.activeSession && (
        <CartDrawer
          session={cart.activeSession}
          mode={cart.activeSession.mode}
          onClose={() => setIsCartOpen(false)}
          onRemoveItem={cart.removeItem}
          onUpdatePrice={cart.updateItemPrice}
          onShowTotal={handleShowTotal}
          onCompleteSession={handleCompleteSession}
          onClearCart={handleClearCart}
          parkedSessions={cart.parkedSessions}
          onSwitchSession={cart.resumeSession}
          onParkSession={cart.parkSession}
          onStartNewSession={cart.startNewSession}
          onRenameSession={cart.renameSession}
          variant="tablet"
        />
      )}

      {/* Show total overlay */}
      {isShowingTotal && cart.activeSession && (
        <ShowTotalOverlay
          items={cart.activeSession.items}
          total={cart.total}
          itemCount={cart.itemCount}
          mode={cart.activeSession.mode}
          onDismiss={() => setIsShowingTotal(false)}
        />
      )}

      {/* Clear cart confirmation */}
      {showClearConfirm && (
        <ConfirmDialog
          title="Clear Cart?"
          message={`This will remove all ${cart.itemCount} item${cart.itemCount !== 1 ? 's' : ''} from the current session.`}
          confirmLabel="Clear"
          onConfirm={confirmClearCart}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}

      {/* Mode switch warning */}
      {pendingModeSwitch && (
        <ConfirmDialog
          title={`Switch to ${pendingModeSwitch === 'buy' ? 'Buy' : 'Sell'} mode?`}
          message={`You have ${cart.itemCount} item${cart.itemCount !== 1 ? 's' : ''} in your ${cart.activeSession?.mode === 'buy' ? 'Buy' : 'Sell'} cart. This session will be parked.`}
          confirmLabel="Switch"
          confirmVariant="primary"
          onConfirm={confirmModeSwitch}
          onCancel={() => setPendingModeSwitch(null)}
        />
      )}

      {/* Session history */}
      {isHistoryOpen && (
        <SessionHistory
          sessions={history.sessions}
          isLoading={history.isLoading}
          onDeleteSession={history.deleteSession}
          onClearAll={history.clearAll}
          onCopyToClipboard={history.copyToClipboard}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}

      {/* Card scan modal */}
      {isCameraOpen && (
        <CameraCapture
          mode={mode}
          buyPercent={settings.buyOfferPercent}
          onClose={() => setIsCameraOpen(false)}
          onCardSelected={handleScanCardSelected}
          onCardForQuote={handleScanCardForQuote}
          onAddToCart={handleScanAddToCart}
          onFallbackSearch={handleScanFallback}
        />
      )}
    </Layout>
  );
}
